import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  Search,
  Download,
  Eye,
  Plus,
  Check,
  X,
  Receipt,
  Upload,
  Euro,
  Calendar,
  Filter,
  Edit,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/ui/pagination";

import { payrollAPI, employeesAPI } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";

export default function ExpenseManagement() {
  const { toast, success: showSuccess, error: showError } = useToast();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    employeeId: "",
    category: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  // Modals
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    montant: "",
    motif: "",
    categorie: "",
    date_depense: "",
    justificatif: null,
  });

  const [editForm, setEditForm] = useState({
    montant: "",
    motif: "",
    categorie: "",
    date_depense: "",
    justificatif: null,
  });

  const statusColors = {
    soumise: "bg-yellow-100 text-yellow-800",
    approuvee: "bg-green-100 text-green-800",
    refusee: "bg-red-100 text-red-800",
    remboursee: "bg-blue-100 text-blue-800",
  };

  const statusLabels = {
    soumise: "Soumise",
    approuvee: "Approuvée",
    refusee: "Refusée",
    remboursee: "Remboursée",
  };

  const categoryLabels = {
    transport: "Transport",
    restauration: "Restauration",
    hebergement: "Hébergement",
    materiel: "Matériel",
    formation: "Formation",
    autre: "Autre",
  };

  useEffect(() => {
    loadExpenses();
    if (user?.role === "admin" || user?.role === "rh") {
      loadEmployees();
    }
  }, [filters, pagination.page]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const response = await payrollAPI.getExpenses(params);
      setExpenses(response.data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.total || 0,
      }));
    } catch (error) {
      console.error("Error loading expenses:", error);
      showError("Erreur lors du chargement des notes de frais");
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await employeesAPI.getAll({ limit: 1000 });
      setEmployees(response.data?.data?.employees || []);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setTimeout(loadExpenses, 300);
  };

  const handleFilterChange = (key, value) => {
    // Convert "all" to empty string for API
    const apiValue = value === "all" ? "" : value;
    setFilters((prev) => ({ ...prev, [key]: apiValue }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    try {
      const expenseData = new FormData();

      // Convert montant to number and validate
      const montant = parseFloat(formData.montant);
      if (isNaN(montant) || montant <= 0) {
        showError("Le montant doit être un nombre positif");
        return;
      }

      // Validate motif
      if (!formData.motif || formData.motif.trim().length === 0) {
        showError("Le motif est requis");
        return;
      }

      if (formData.motif.trim().length > 500) {
        showError("Le motif ne peut pas dépasser 500 caractères");
        return;
      }

      // Add all required fields
      expenseData.append("montant", montant.toString());
      expenseData.append("motif", formData.motif.trim());

      // Add optional fields only if they have values
      if (formData.categorie && formData.categorie.trim()) {
        expenseData.append("categorie", formData.categorie.trim());
      }

      if (formData.date_depense) {
        expenseData.append("date_depense", formData.date_depense);
      }

      if (formData.justificatif) {
        expenseData.append("justificatif", formData.justificatif);
      }
      await payrollAPI.createExpense(expenseData);
      showSuccess("Note de frais créée avec succès !");
      setShowCreateForm(false);
      setFormData({
        montant: "",
        motif: "",
        categorie: "",
        date_depense: "",
        justificatif: null,
      });
      loadExpenses();
    } catch (error) {
      console.error("Error creating expense:", error);
      showError("Erreur lors de la création de la note de frais");
    }
  };

  const handleApproveExpense = async (approve = true) => {
    if (!selectedExpense) return;

    try {
      setLoading(true);
      const response = await payrollAPI.approveExpense(selectedExpense.id, {
        statut: approve ? "approuvee" : "refusee",
        commentaire_approbation: selectedExpense.commentaire || "",
      });

      if (response.data.success) {
        showSuccess(
          approve
            ? "Note de frais approuvée avec succès"
            : "Note de frais refusée avec succès"
        );
        setShowApprovalModal(false);
        setSelectedExpense(null);
        loadExpenses(); // Refresh the list
      } else {
        showError(response.data.message || "Erreur lors de l'approbation");
      }
    } catch (error) {
      console.error("Error approving expense:", error);
      showError("Erreur lors de l'approbation de la note de frais");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = { ...filters, search: searchTerm };
      const response = await payrollAPI.exportExpenses(params);

      // Create blob and download link
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "notes_frais_export.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess("Export réalisé avec succès !");
    } catch (error) {
      console.error("Error exporting expenses:", error);
      showError("Erreur lors de l'export");
    }
  };

  const canManageExpenses = user?.role === "admin" || user?.role === "rh";
  const canApprove = canManageExpenses;
  const isEmployee = user?.role === "employe";

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Receipt className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Notes de Frais
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {pagination.total}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Euro className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Montant Total
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {expenses
                    .reduce((sum, e) => sum + (parseFloat(e.montant) || 0), 0)
                    .toLocaleString("fr-MA", {
                      style: "currency",
                      currency: "MAD",
                    })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En Attente</p>
                <p className="text-2xl font-bold text-gray-900">
                  {expenses.filter((e) => e.statut === "soumise").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Check className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Approuvées</p>
                <p className="text-2xl font-bold text-gray-900">
                  {expenses.filter((e) => e.statut === "approuvee").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <CardTitle>Notes de Frais</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Créer Note de Frais</span>
              </Button>
              {canManageExpenses && (
                <Button
                  variant="outline"
                  onClick={handleExport}
                  className="flex items-center space-x-2"
                >
                  <Download className="h-4 w-4" />
                  <span>Exporter</span>
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {canManageExpenses && (
              <Select
                value={filters.employeeId || "all"}
                onValueChange={(value) =>
                  handleFilterChange("employeeId", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un employé" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  {Array.isArray(employees) &&
                    employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.prenom} {employee.nom}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}

            <Select
              value={filters.category || "all"}
              onValueChange={(value) => handleFilterChange("category", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Catégorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {Object.keys(categoryLabels).map((category) => (
                  <SelectItem key={category} value={category}>
                    {categoryLabels[category]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.status || "all"}
              onValueChange={(value) => handleFilterChange("status", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {Object.keys(statusLabels).map((status) => (
                  <SelectItem key={status} value={status}>
                    {statusLabels[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              placeholder="Date de début"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
            />

            <Input
              type="date"
              placeholder="Date de fin"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
            />
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  {canManageExpenses && <TableHead>Employé</TableHead>}
                  <TableHead>Description</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Montant</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Approuvé par</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={canManageExpenses ? 8 : 7}
                      className="text-center py-8"
                    >
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2">Chargement...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : expenses.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={canManageExpenses ? 8 : 7}
                      className="text-center py-8"
                    >
                      <div className="text-gray-500">
                        <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>Aucune note de frais trouvée</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  expenses.map((expense) => (
                    <TableRow key={expense.id}>
                      {canManageExpenses && (
                        <TableCell>
                          <div>
                            <p className="font-medium">
                              {expense.employe?.prenom} {expense.employe?.nom}
                            </p>
                            <p className="text-sm text-gray-500">
                              {expense.employe?.email}
                            </p>
                          </div>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="max-w-xs">
                          <p
                            className="truncate"
                            title={expense.motif || expense.description}
                          >
                            {expense.motif || expense.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{categoryLabels[expense.categorie]}</TableCell>
                      <TableCell>
                        {parseFloat(expense.montant || 0).toLocaleString(
                          "fr-MA",
                          {
                            style: "currency",
                            currency: "MAD",
                          }
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(expense.date_depense).toLocaleDateString(
                          "fr-FR"
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[expense.statut]}>
                          {statusLabels[expense.statut]}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {expense.approbateur ? (
                          <div>
                            <p className="font-medium">
                              {expense.approbateur.prenom}{" "}
                              {expense.approbateur.nom}
                            </p>
                            <p className="text-gray-500">
                              {new Date(
                                expense.date_approbation
                              ).toLocaleDateString("fr-MA")}
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-400">En attente</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          {/* Details button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedExpense(expense);
                              setShowDetailsModal(true);
                            }}
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {canApprove && expense.statut === "soumise" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-600 hover:bg-green-50"
                                onClick={() => {
                                  setSelectedExpense(expense);
                                  setShowApprovalModal(true);
                                }}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-600 hover:bg-red-50"
                                onClick={() => {
                                  setSelectedExpense(expense);
                                  handleApproveExpense(false);
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}

                          {isEmployee &&
                            expense.employe_id === user?.id &&
                            expense.statut === "soumise" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                  onClick={() => {
                                    setEditingExpense(expense);
                                    setEditForm({
                                      montant:
                                        expense.montant?.toString() || "",
                                      motif:
                                        expense.motif ||
                                        expense.description ||
                                        "",
                                      categorie: expense.categorie || "",
                                      date_depense: expense.date_depense
                                        ? new Date(expense.date_depense)
                                            .toISOString()
                                            .slice(0, 10)
                                        : "",
                                      justificatif: null,
                                    });
                                    setShowEditModal(true);
                                  }}
                                  title="Modifier"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-600 hover:bg-red-50"
                                  onClick={() => {
                                    setEditingExpense(expense);
                                    setShowDeleteModal(true);
                                  }}
                                  title="Supprimer"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.total > pagination.limit && (
            <div className="mt-6 flex justify-center">
              <Pagination
                currentPage={pagination.page}
                totalPages={Math.ceil(pagination.total / pagination.limit)}
                onPageChange={(page) =>
                  setPagination((prev) => ({ ...prev, page }))
                }
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Expense Modal */}
      {showCreateForm && (
        <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto z-[9999]">
            <DialogHeader>
              <DialogTitle>Créer Note de Frais</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateExpense} className="space-y-4">
              <div>
                <Label htmlFor="motif">Motif</Label>
                <Textarea
                  id="motif"
                  value={formData.motif}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      motif: e.target.value,
                    }))
                  }
                  required
                  maxLength={500}
                  placeholder="Précisez le motif de votre note de frais..."
                />
                <p className="text-xs text-gray-500 text-right mt-1">
                  {formData.motif.length}/500 caractères
                </p>
              </div>

              <div>
                <Label htmlFor="montant">Montant</Label>
                <Input
                  id="montant"
                  type="number"
                  step="0.01"
                  value={formData.montant}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      montant: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div>
                <Label htmlFor="categorie">Catégorie</Label>
                <Select
                  value={formData.categorie}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, categorie: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="z-[10000]">
                    {Object.keys(categoryLabels).map((category) => (
                      <SelectItem key={category} value={category}>
                        {categoryLabels[category]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="date_depense">Date</Label>
                <Input
                  id="date_depense"
                  type="date"
                  value={formData.date_depense}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      date_depense: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div>
                <Label htmlFor="justificatif">Reçu</Label>
                <Input
                  id="justificatif"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      justificatif: e.target.files[0],
                    }))
                  }
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  Annuler
                </Button>
                <Button type="submit">Créer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedExpense && (
        <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Approuver Note de Frais</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Motif</p>
                <p className="font-medium">
                  {selectedExpense.motif || selectedExpense.description}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-600">Montant</p>
                <p className="font-medium">
                  {parseFloat(selectedExpense.montant).toLocaleString("fr-MA", {
                    style: "currency",
                    currency: "MAD",
                  })}
                </p>
              </div>

              <div>
                <Label htmlFor="commentaire">Commentaire</Label>
                <Textarea
                  id="commentaire"
                  value={selectedExpense.commentaire || ""}
                  onChange={(e) =>
                    setSelectedExpense((prev) => ({
                      ...prev,
                      commentaire: e.target.value,
                    }))
                  }
                  placeholder="Commentaire d'approbation..."
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowApprovalModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-600 hover:bg-red-50"
                  onClick={() => handleApproveExpense(false)}
                >
                  Rejeter
                </Button>
                <Button onClick={() => handleApproveExpense(true)}>
                  Approuver
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Expense Modal (Employee) */}
      {showEditModal && editingExpense && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto z-[9999]">
            <DialogHeader>
              <DialogTitle>Modifier la Note de Frais</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const fd = new FormData();
                  if (editForm.montant) fd.append("montant", editForm.montant);
                  if (editForm.motif) fd.append("motif", editForm.motif);
                  if (editForm.categorie)
                    fd.append("categorie", editForm.categorie);
                  if (editForm.date_depense)
                    fd.append("date_depense", editForm.date_depense);
                  if (editForm.justificatif)
                    fd.append("justificatif", editForm.justificatif);

                  await payrollAPI.updateExpense(editingExpense.id, fd);
                  showSuccess("Note de frais mise à jour avec succès");
                  setShowEditModal(false);
                  setEditingExpense(null);
                  loadExpenses();
                } catch (err) {
                  console.error("Error updating expense:", err);
                  showError(
                    err.response?.data?.error ||
                      "Erreur lors de la mise à jour de la note de frais"
                  );
                }
              }}
              className="space-y-4"
            >
              <div>
                <Label htmlFor="edit_motif">Motif</Label>
                <Textarea
                  id="edit_motif"
                  value={editForm.motif}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, motif: e.target.value }))
                  }
                  maxLength={500}
                />
              </div>

              <div>
                <Label htmlFor="edit_montant">Montant</Label>
                <Input
                  id="edit_montant"
                  type="number"
                  step="0.01"
                  value={editForm.montant}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      montant: e.target.value,
                    }))
                  }
                  required
                />
              </div>

              <div>
                <Label htmlFor="edit_categorie">Catégorie</Label>
                <Select
                  value={editForm.categorie}
                  onValueChange={(value) =>
                    setEditForm((prev) => ({ ...prev, categorie: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="z-[10000]">
                    {Object.keys(categoryLabels).map((category) => (
                      <SelectItem key={category} value={category}>
                        {categoryLabels[category]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit_date">Date</Label>
                <Input
                  id="edit_date"
                  type="date"
                  value={editForm.date_depense}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      date_depense: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="edit_justificatif">Justificatif</Label>
                <Input
                  id="edit_justificatif"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      justificatif: e.target.files?.[0] || null,
                    }))
                  }
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                >
                  Annuler
                </Button>
                <Button type="submit">Enregistrer</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Expense Modal (Employee) */}
      {showDeleteModal && editingExpense && (
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Supprimer la Note de Frais</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p>
                Êtes-vous sûr de vouloir supprimer cette note de frais ? Cette
                action est irréversible.
              </p>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    try {
                      await payrollAPI.deleteExpense(editingExpense.id);
                      showSuccess("Note de frais supprimée avec succès");
                      setShowDeleteModal(false);
                      setEditingExpense(null);
                      loadExpenses();
                    } catch (err) {
                      console.error("Error deleting expense:", err);
                      showError(
                        err.response?.data?.error ||
                          "Erreur lors de la suppression de la note de frais"
                      );
                    }
                  }}
                >
                  Supprimer définitivement
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Details Modal */}
      {showDetailsModal && selectedExpense && (
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Receipt className="h-5 w-5 text-blue-600" />
                <span>Détails de la Note de Frais</span>
              </DialogTitle>
            </DialogHeader>

            <div className="rounded-lg border p-4 bg-gray-50">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600">Employé</p>
                  <p className="font-semibold">
                    {selectedExpense.employe?.prenom}{" "}
                    {selectedExpense.employe?.nom}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Montant</p>
                  <p className="font-semibold text-green-700">
                    {parseFloat(selectedExpense.montant || 0).toLocaleString(
                      "fr-MA",
                      {
                        style: "currency",
                        currency: "MAD",
                      }
                    )}
                  </p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">
                    {new Date(selectedExpense.date_depense).toLocaleDateString(
                      "fr-FR"
                    )}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">
                    {categoryLabels[selectedExpense.categorie]}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge className={statusColors[selectedExpense.statut]}>
                    {statusLabels[selectedExpense.statut]}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <p className="text-sm text-gray-600">Motif / Description</p>
              <p className="mt-1 whitespace-pre-wrap">
                {selectedExpense.motif || selectedExpense.description}
              </p>
            </div>

            {(selectedExpense.justificatif_chemin || selectedExpense.id) && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Justificatif</p>
                <Button
                  variant="outline"
                  onClick={() =>
                    window.open(
                      `${
                        import.meta.env.VITE_API_URL ||
                        "http://localhost:3001/api/v1"
                      }/payroll/expenses/${selectedExpense.id}/receipt`,
                      "_blank"
                    )
                  }
                >
                  Ouvrir le justificatif
                </Button>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDetailsModal(false)}
              >
                Fermer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
